import type { Scene } from '@babylonjs/core/scene';
import type { AssetContainer } from '@babylonjs/core/assetContainer';
import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader';
import { registerBuiltInLoaders } from '@babylonjs/loaders/dynamic';
import type { AssetRegistry } from './AssetRegistry';
import type { AssetId, PreloadPolicy } from './AssetTypes';
import { RENDERING_CONFIG, type LandmarkFogConfig } from '../config/rendering.config';
import { BEHIND_FENCE, applyDecalOffset } from '../rendering/decals';
import { calmFinePattern } from '../rendering/finePatterns';
import { stretchFog } from '../rendering/fogStretch';

let loadersRegistered = false;

/**
 * Meshes dropped from published GLBs at load time. ENV_SHELL_A_BANNER_BOARD: the empty board on the first slab edge of
 * the shell – its banner moved up to the second floor (AD_SHELL_SLAB), the board stayed behind as a dark rectangle
 * (user 2026-10-04: "komplett entfernen").
 */
const RETIRED_MESHES: readonly string[] = ['ENV_SHELL_A_BANNER_BOARD'];

/** Models and their shared textures live below this path (public/assets). */
const ASSET_TREE = '/assets/';

/**
 * Published GLBs point at the shared texture files (public/assets/textures/shared, written by
 * tools/pipeline/glb-textures.mjs) with "../" paths, which the glTF loader rejects by default.
 * Allowed here – but only into the asset tree on the GLB's own origin.
 */
function resolveAssetUrl(url: string, rootUrl?: string): Promise<string> {
  const resolved = new URL(url, window.location.href);
  const base = new URL(rootUrl ?? url, window.location.href);
  if (resolved.origin !== base.origin || !resolved.pathname.startsWith(ASSET_TREE)) {
    return Promise.reject(new Error(`glTF resource outside ${ASSET_TREE}: ${url}`));
  }
  return Promise.resolve(resolved.href);
}

/**
 * Loads exported GLBs into cached AssetContainers. Each GLB is fetched and
 * parsed once; every placement instantiates from the cached container.
 * The glTF loader itself is registered lazily (dynamic import on first use).
 */
export class AssetLoader {
  private readonly containers = new Map<AssetId, AssetContainer>();
  private readonly inFlight = new Map<AssetId, Promise<AssetContainer>>();
  private readonly failed = new Set<AssetId>();
  private disposed = false;
  private readonly instancingReady = new Set<AssetId>();

  constructor(
    private readonly scene: Scene,
    private readonly registry: AssetRegistry,
    /** Landmark assets (ad gantry) keep a stretched fog band – visible as far as their banner. */
    private readonly landmarkFog: LandmarkFogConfig = RENDERING_CONFIG.landmarkFog,
  ) {
    if (!loadersRegistered) {
      registerBuiltInLoaders();
      loadersRegistered = true;
    }
  }

  get(id: AssetId): AssetContainer | undefined {
    return this.containers.get(id);
  }

  /**
   * Prepares a loaded container for hardware instancing: the original meshes
   * are added to the scene once, invisible – every placement is an instance of
   * them (one draw call per mesh for all placements).
   */
  prepareInstancing(id: AssetId): void {
    const container = this.containers.get(id);
    if (!container || this.instancingReady.has(id)) return;
    container.addAllToScene();
    for (const mesh of container.meshes) {
      mesh.isVisible = false;
      mesh.isPickable = false;
    }
    this.instancingReady.add(id);
  }

  hasFailed(id: AssetId): boolean {
    return this.failed.has(id);
  }

  load(id: AssetId): Promise<AssetContainer> {
    const cached = this.containers.get(id);
    if (cached) return Promise.resolve(cached);
    const pending = this.inFlight.get(id);
    if (pending) return pending;

    const entry = this.registry.get(id);
    if (entry.source.kind !== 'glb') return Promise.reject(new Error(`${id} is not a GLB asset`));

    const promise = LoadAssetContainerAsync(entry.source.url, this.scene, { pluginOptions: { gltf: { preprocessUrlAsync: resolveAssetUrl } } })
      .then((container) => {
        if (this.disposed) {
          container.dispose();
          throw new Error(`AssetLoader disposed while loading ${id}`);
        }
        // parts retired after the GLB was built (removed from the Blender scripts too, gone with the next export)
        for (let i = container.meshes.length - 1; i >= 0; i--) {
          const mesh = container.meshes[i]!;
          if (RETIRED_MESHES.some((name) => mesh.name.startsWith(name))) {
            container.meshes.splice(i, 1);
            mesh.dispose(false, false);
          }
        }
        for (const material of container.materials) {
          applyDecalOffset(material);
          calmFinePattern(material); // fence mesh, scaffold net: no flicker in motion
        }
        // roadside nets first among the transparent surfaces: always behind the fence, no order flip while running
        for (const mesh of container.meshes) if (mesh.material && BEHIND_FENCE.test(mesh.material.name)) mesh.alphaIndex = 0;
        // the GLB's own materials – the greybox fallback shares library materials and fogs normally
        if (entry.metadata.tags?.includes(this.landmarkFog.tag)) for (const material of container.materials) stretchFog(material, this.landmarkFog.scale);
        // The glTF loader auto-starts the first clip on the container's own (never rendered) nodes –
        // visuals play their cloned groups, so the originals would only cost time every frame.
        for (const group of container.animationGroups) group.stop();
        this.containers.set(id, container);
        return container;
      })
      .catch((error: unknown) => {
        this.failed.add(id);
        throw error instanceof Error ? error : new Error(String(error));
      })
      .finally(() => this.inFlight.delete(id));
    this.inFlight.set(id, promise);
    return promise;
  }

  /**
   * Loads all GLB assets with the given preload policies. Failures are
   * reported but do not abort – the greybox fallback keeps the game playable.
   */
  async preload(policies: PreloadPolicy[], onProgress?: (loaded: number, total: number) => void): Promise<string[]> {
    const ids = this.registry
      .withPreload(...policies)
      .filter((entry) => entry.source.kind === 'glb')
      .map((entry) => entry.id);
    const errors: string[] = [];
    let loaded = 0;
    onProgress?.(0, ids.length);
    await Promise.all(
      ids.map((id) =>
        this.load(id)
          .catch((error: Error) => errors.push(`${id}: ${error.message}`))
          .finally(() => onProgress?.(++loaded, ids.length)),
      ),
    );
    return errors;
  }

  dispose(): void {
    this.disposed = true;
    for (const container of this.containers.values()) container.dispose();
    this.containers.clear();
  }
}
